// WorldMap.jsx
import { useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from 'react-simple-maps';
import { Box, Typography, Zoom, Stack, Chip, alpha } from '@mui/material';

const WorldMap = () => {
  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2.0.2/countries-110m.json';

  // Coins placed on their actual countries of origin - NO country labels on map
  const countryCoins = [
    // AFRICA
    {
      name: 'South African Krugerrand',
      country: 'South Africa',
      coordinates: [26.5, -31.0], // Far right and south
      coin: '/conticoins/SAGoldKrug1ozRev.jpg',
      description: 'First modern bullion coin, introduced in 1967',
      metal: 'Gold',
      continent: 'Africa',
      url: 'https://www.samint.co.za',
    },
    {
      name: 'Botswana 10 Thebe',
      country: 'Botswana',
      coordinates: [24.5, -20.5], // MOVED UP: Changed latitude from -22.5 to -19.5 (further north)
      coin: '/conticoins/10 Thebe (Independence) - Botswana.jpg',
      description: 'Commemorative independence coin of Botswana',
      metal: 'Silver',
      continent: 'Africa',
      url: 'https://www.bankofbotswana.bw',
    },

    // AUSTRALIA/OCEANIA
    {
      name: 'Australian Gold Kangaroo',
      country: 'Australia',
      coordinates: [149, -35],
      coin: '/conticoins/Australian Gold Kangaroo.jpg',
      description: 'Australian legal tender coin featuring the iconic kangaroo',
      metal: 'Gold',
      continent: 'Australia',
      url: 'https://www.ramint.gov.au',
    },

    // ASIA
    {
      name: 'Chinese Gold Panda',
      country: 'China',
      coordinates: [116.4, 39.9],
      coin: '/conticoins/Asia – Panda.jpg',
      description: 'China\'s official bullion coin featuring the Giant Panda',
      metal: 'Gold',
      continent: 'Asia',
      url: 'https://www.chinagoldcoin.net',
    },

    // EUROPE
    {
      name: 'Austrian Philharmonic',
      country: 'Austria',
      coordinates: [16.37, 48.21],
      coin: '/conticoins/Europe (Austria) – Philharmonic.jpg',
      description: 'Music-inspired coin featuring Vienna Philharmonic instruments',
      metal: 'Gold',
      continent: 'Europe',
      url: 'https://www.muenzeoesterreich.at',
    },
    {
      name: 'Austrian Philharmonic (2026)',
      country: 'Austria',
      coordinates: [16.37, 47.21],
      coin: '/conticoins/935181_120303-1-oz-wiener-philharmoniker-goldmuenze-100-euro-oesterreich-2026-1.png',
      description: '2026 Vienna Philharmonic 100 Euro coin',
      metal: 'Gold',
      continent: 'Europe',
      url: 'https://www.muenzeoesterreich.at',
    },
    {
      name: 'British Britannia',
      country: 'United Kingdom',
      coordinates: [-0.13, 51.51],
      coin: '/conticoins/Europe (UK) – Britannia (your 3144 image likely this).jpg',
      description: 'UK\'s flagship bullion coin featuring Britannia',
      metal: 'Gold',
      continent: 'Europe',
      url: 'https://www.royalmint.com',
    },

    // NORTH AMERICA
    {
      name: 'American Gold Eagle',
      country: 'United States',
      coordinates: [-100, 39],
      coin: '/conticoins/North America – American Eagle.jpg',
      description: 'Official US bullion coin featuring Lady Liberty',
      metal: 'Gold',
      continent: 'North America',
      url: 'https://www.usmint.gov',
    },
 
    {
      name: 'Canadian Gold Maple Leaf',
      country: 'Canada',
      coordinates: [-75.7, 45.5],
      coin: '/conticoins/1oz Gold Maple Leaf Coin.png',
      description: 'World-renowned 99.99% pure gold coin',
      metal: 'Gold',
      continent: 'North America',
      url: 'https://www.mint.ca',
    },
  ];

  const getMetalColor = (metal) => {
    switch(metal) {
      case 'Gold': return '#FFD700';
      case 'Silver': return '#C0C0C0';
      case 'Platinum': return '#E5E4E2';
      case 'Palladium': return '#E8E8E8';
      case 'Rhodium': return '#D3D3D3';
      default: return '#FFD700';
    }
  };

  const handleMouseEnter = (event, coin) => {
    setTooltipContent(coin);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseLeave = () => {
    setTooltipContent(null);
  };

  const handleCoinClick = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        bgcolor: '#000066',
        overflow: 'hidden',
      }}
    >
      <ComposableMap
        projectionConfig={{
          scale: 240,
          center: [0, 15],
        }}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#000066',
        }}
      >
        <Geographies geography={geoUrl}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill="#0a0a3a"
                stroke="rgba(255, 255, 255, 0.15)"
                strokeWidth={0.6}
                style={{
                  default: { outline: 'none' },
                  hover: { fill: 'rgba(255, 215, 0, 0.1)', outline: 'none' },
                  pressed: { outline: 'none' },
                }}
              />
            ))
          }
        </Geographies>

        {/* Coins placed on their actual countries - INCREASED SIZE */}
        {countryCoins.map((coin, idx) => (
          <Marker
            key={idx}
            coordinates={coin.coordinates}
            onMouseEnter={(e) => handleMouseEnter(e, coin)}
            onMouseLeave={handleMouseLeave}
          >
            <g 
              style={{ cursor: coin.url ? 'pointer' : 'default' }}
              onClick={() => handleCoinClick(coin.url)}
            >
              {/* Outer glow - increased */}
              <circle
                r={40}
                fill="rgba(212, 175, 55, 0.08)"
                stroke="rgba(212, 175, 55, 0.2)"
                strokeWidth={1.2}
              />
              {/* Pulsing ring - increased */}
              <circle
                r={36}
                fill="none"
                stroke="rgba(212, 175, 55, 0.3)"
                strokeWidth={1}
              >
                <animate
                  attributeName="r"
                  from={36}
                  to={50}
                  dur="2.5s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  from="0.3"
                  to="0"
                  dur="2.5s"
                  repeatCount="indefinite"
                />
              </circle>
              {/* Coin Image - INCREASED SIZE to 65px diameter */}
              <foreignObject
                x={-32.5}
                y={-32.5}
                width={65}
                height={65}
                style={{
                  borderRadius: '50%',
                  overflow: 'hidden',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.5), 0 0 0 3px rgba(212,175,55,0.4)',
                  border: `2px solid ${getMetalColor(coin.metal)}`,
                }}
              >
                <Box
                  component="img"
                  src={coin.coin}
                  alt={coin.name}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '50%',
                  }}
                  onError={(e) => {
                    e.target.src = '/conticoins/North%20America%20%E2%80%93%20American%20Eagle.jpg';
                    e.target.onerror = null;
                  }}
                />
              </foreignObject>
            </g>
          </Marker>
        ))}
      </ComposableMap>

      {/* Tooltip - shows all information including country */}
      <Zoom in={!!tooltipContent}>
        <Box
          sx={{
            position: 'fixed',
            left: tooltipPosition.x + 15,
            top: tooltipPosition.y - 140,
            bgcolor: alpha('#000066', 0.95),
            backdropFilter: 'blur(12px)',
            border: `1px solid ${getMetalColor(tooltipContent?.metal || '#FFD700')}`,
            borderRadius: 2,
            p: 1.5,
            minWidth: 270,
            maxWidth: 300,
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 10px 30px rgba(0,0,0,0.6)',
          }}
        >
          {tooltipContent && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: getMetalColor(tooltipContent.metal),
                  }}
                />
                <Typography variant="subtitle2" sx={{ color: getMetalColor(tooltipContent.metal), fontWeight: 700 }}>
                  {tooltipContent.metal}
                </Typography>
                <Chip
                  label={tooltipContent.continent}
                  size="small"
                  sx={{
                    bgcolor: alpha('#FFD700', 0.15),
                    color: '#FFD700',
                    fontSize: '0.55rem',
                    height: 18,
                  }}
                />
              </Stack>
              
              <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 0.5, fontSize: '0.85rem' }}>
                {tooltipContent.name}
              </Typography>
              
              <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.6), display: 'block', fontSize: '0.7rem' }}>
                Country: {tooltipContent.country}
              </Typography>
              
              <Typography variant="caption" sx={{ color: alpha('#FFFFFF', 0.7), display: 'block', fontSize: '0.7rem', mt: 0.5 }}>
                {tooltipContent.description}
              </Typography>
            </>
          )}
        </Box>
      </Zoom>
    </Box>
  );
};

export default WorldMap;